import { syntaxTokenizer, type FormulaParser, type IToken } from "@datapolarna/formulaparser";
import { LanguageSupport, StreamLanguage } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";
import { CompletionContext, type Completion, type CompletionResult } from "@codemirror/autocomplete";
import type { EditorView } from "codemirror";

interface ParserState {
  currentToken: IToken | null;
}

export interface FormulaLanguageDescription {
  variables: FormulaVariable[]
}

export interface FormulaVariable {
  name: string;
  children?: FormulaVariable[];
  info?: string;
  details?: string;
}

export interface FormulaLanguage {
  formulaParser: FormulaParser;
  language: LanguageSupport;
}

export function createLanguage(formulaParser: FormulaParser, lang: FormulaLanguageDescription) : FormulaLanguage {
  const excelLanguage = StreamLanguage.define<ParserState>({
      startState: () => {
          return { currentToken: null };
      },
      token: (stream, state) => {

          const tokenType = syntaxTokenizer({
              peek: () => stream.peek() ?? null,
              isEnd: () => stream.eol(),
              match: (pattern, consume) => {
                  const result = stream.match(pattern, consume);

                  if (result === null) {
                      return null;
                  }

                  if (typeof result == "boolean") {
                      return null;
                  }

                  if (result.length === 0)
                      return null;

                  return result[0];
              },
              next: () => {
                const value = stream.next();
                if (typeof value === 'string') return value;
                return null;
              },
              get current() {
                  return state.currentToken;
              }
          });

          if (!tokenType.isWhitespace) {

              const token: IToken = {
                  value: stream.string.substring(stream.start, stream.pos),
                  type: tokenType,
                  from: stream.start,
                  to: stream.pos
              };

              state.currentToken = token;
          }

          return tokenType.tag;

      },
      tokenTable: {
          "string": t.string,
          "number": t.number,
          "operator": t.operator,
          "variable": t.variableName,
          "reference-name": t.annotation,
          "function-name": t.function(t.variableName),
          "start quote": t.string,
          "end quote": t.string,
          "bracket": t.squareBracket,
          "start-parenthesis": t.paren,
          "comma": t.punctuation,
          "error": t.invalid,
          "boolean": t.bool
      }
  });

  const autoComplete = excelLanguage.data.of({
      autocomplete: autoCompleteFunc
  });

  formulaParser.clearVariables();

  traverseLanguage(lang, (node, path) => {

    const children = node.children;
    const type = children && children.length > 0 ? 'class' : 'variable';

    formulaParser.addSyntax({
      path: path,
      details: node.details,
      info: node.info,
      type: type
    });
  });

  return {
    language: new LanguageSupport(excelLanguage, [autoComplete]),
    formulaParser: formulaParser
  };

  function traverseLanguage(lang: FormulaLanguageDescription, visit: (variable: FormulaVariable, path: string) => void) {

    traverse(lang.variables, '');

    function traverse(variables: FormulaVariable[], parentPath: string) {
      for (const variable of variables) {

        let path;

        if (parentPath.length === 0) {
          path = variable.name;
        } else {
          path = parentPath + "." + variable.name;
        }

        visit(variable, path);
        const children = variable.children;
        if (!children || children.length === 0) continue;

        traverse(children, path);
      }
    }

  }

  function autoCompleteFunc(context: CompletionContext): CompletionResult | null {

      const options: Completion[] = [];

      const result: CompletionResult = {
          from: context.pos,
          options: options,
          validFor: /^[\w$]*$/
      };

      const path = context.matchBefore(/(\w|\.)*/);

      if (path) {
        const index = path.text.lastIndexOf('.');
        result.from = index > -1 ? path.from + index + 1 : path.from;
        findOptions(path.text, options, formulaParser);
      } else if (context.explicit) {
        findOptions('', options, formulaParser);
      }


      return result;
  }
}

function findOptions(path: string,
  options: Completion[],
  parser: FormulaParser) {

  const result = parser.autocompleteOptions(path);

  for (const option of result) {
    const completion: Completion = {
      label: option.label,
      info: option.info,
      detail: option.detail,
      type: option.type,
    };

    options.push(completion);

    switch(option.type) {
      case 'function':
        completion.apply = (view: EditorView, completion, from: number, to: number) => {

          const text = completion.label + "()";
          const parenIndex = from + text.length - 1;

          const tran = view.state.update({
              changes: {
                  from: from,
                  to: to,
                  insert: completion.label + "()"
              },
              selection: {
                  head: parenIndex,
                  anchor: parenIndex
              }
          });

          view.dispatch(tran);
        };
        completion.boost = 1;
        break;
      case 'class':
        completion.boost = 2;
        break;
      case 'variable':
        completion.boost = 3;
        break;
    }
  }
}
